import React, { useRef, useEffect, useState } from "react";
import {
  Star,
  Quote,
  MapPin,
  User,
  ThumbsUp,
  MessageCircle,
  ArrowRight,
  ShoppingBag,
  AlertCircle,
} from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useCurrency } from "@/context/CurrencyContext";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import Autoplay from "embla-carousel-autoplay";
import {
  getPublishedTestimonials,
  CustomerTestimonial,
} from "@/services/testimonialsService";
import { fetchProductById } from "@/services/productService";
import { Product } from "@/data/products";
import LoadingSpinner from "./LoadingSpinner";
import ProductImage from "./ProductImage";

const Testimonials = () => {
  const [isReady, setIsReady] = useState(false);
  const [testimonials, setTestimonials] = useState<CustomerTestimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [productsData, setProductsData] = useState<Record<string, Product>>({});
  const plugin = useRef(null);
  const { formatPrice, selectedCurrency } = useCurrency();

  useEffect(() => {
    // Initialize the plugin only after component is mounted
    plugin.current = Autoplay({
      delay: 10000,
      stopOnInteraction: false,
    });

    // Fetch testimonials and their associated products from Firebase
    const fetchTestimonialsAndProducts = async () => {
      try {
        setError(null);
        const publishedTestimonials = await getPublishedTestimonials();

        // Debug logging
        console.log("Fetched testimonials:", publishedTestimonials);

        // Extract unique product IDs from testimonials
        const productIds = [
          ...new Set(
            publishedTestimonials
              .map((t) => t.productId)
              .filter(Boolean) as string[]
          ),
        ];

        console.log("Product IDs to fetch:", productIds);

        // Fetch products for these IDs
        const productFetches = productIds.map(async (productId) => {
          try {
            const product = await fetchProductById(productId);
            return { productId, product };
          } catch (error) {
            console.error(`Error fetching product ${productId}:`, error);
            return { productId, product: null };
          }
        });

        const productResults = await Promise.all(productFetches);

        // Build products data map
        const fetchedProductsData: Record<string, Product> = {};
        productResults.forEach(({ productId, product }) => {
          if (product) {
            fetchedProductsData[productId] = product;
          }
        });

        console.log("Fetched products data:", fetchedProductsData);

        publishedTestimonials.forEach((testimonial, index) => {
          console.log(`Testimonial ${index}:`, {
            name: testimonial.customerName,
            bgColor: testimonial.bgColor,
            bgColorType: typeof testimonial.bgColor,
            bgColorLength: testimonial.bgColor?.length,
            productId: testimonial.productId,
            productExists: testimonial.productId
              ? fetchedProductsData.hasOwnProperty(testimonial.productId)
              : "No product ID",
          });
        });

        setProductsData(fetchedProductsData);
        setTestimonials(publishedTestimonials);
      } catch (error) {
        console.error("Error fetching testimonials and products:", error);
        setError("Failed to load testimonials. Please try again later.");
      } finally {
        setLoading(false);
        setIsReady(true);
      }
    };

    fetchTestimonialsAndProducts();
  }, []);

  // Helper function to get customer initials
  const getCustomerInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Helper function to get avatar background color with robust fallback
  const getAvatarBackgroundColor = (bgColor?: string) => {
    // Default gradient if no bgColor is provided or if it's invalid
    const defaultGradient = "from-blue-500 to-indigo-600";

    // Check if bgColor exists and is a valid string
    if (!bgColor || typeof bgColor !== "string" || bgColor.trim() === "") {
      console.log("Using default gradient:", defaultGradient);
      return defaultGradient;
    }

    // Check if it looks like a valid Tailwind gradient
    if (bgColor.includes("from-") && bgColor.includes("to-")) {
      console.log("Using provided gradient:", bgColor);
      return bgColor;
    }

    // If it's not a valid gradient format, use default
    console.log(
      "Invalid gradient format, using default:",
      bgColor,
      "->",
      defaultGradient
    );
    return defaultGradient;
  };

  // Helper function to get linked product data
  const getLinkedProduct = (productId?: string) => {
    console.log("getLinkedProduct called with:", {
      productId,
      productIdType: typeof productId,
      hasProductId: !!productId,
      productExists: productId ? productsData.hasOwnProperty(productId) : false,
      availableProducts: Object.keys(productsData),
    });

    // If no product ID or product doesn't exist in our fetched data, return null
    // This will make the testimonial display as testimonial-only
    if (!productId || !productsData[productId]) {
      console.log("Returning null - no product match for:", productId);
      return null;
    }

    const product = productsData[productId];
    const result = {
      id: productId,
      name: product.name,
      price: product.price,
      onSalePrice: product.onSalePrice,
      image: product.image,
      onSale: product.onSale || false,
      isNew: product.isNew || false,
      isBestSeller: product.isBestSeller || false,
    };

    console.log("Returning linked product:", result);
    return result;
  };

  if (loading) {
    return (
      <div className="relative bg-gradient-to-br from-gray-50 via-white to-gray-100 py-20 sm:py-24 overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jamaica-green"></div>
            <span className="ml-4 text-gray-600">Loading testimonials...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative bg-gradient-to-br from-gray-50 via-white to-gray-100 py-20 sm:py-24 overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <MessageCircle className="h-16 w-16 text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Unable to Load Testimonials
            </h3>
            <p className="text-gray-600 max-w-md">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (testimonials.length === 0) {
    return (
      <div className="relative bg-gradient-to-br from-gray-50 via-white to-gray-100 py-20 sm:py-24 overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Star className="h-16 w-16 text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No Customer Reviews Yet
            </h3>
            <p className="text-gray-600 max-w-md">
              We're working hard to provide amazing products and experiences.
              Customer reviews will appear here once they start sharing their
              feedback.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-gradient-to-br from-gray-50 via-white to-gray-100 py-20 sm:py-24 overflow-hidden">
      {/* Enhanced Background Pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Curved Lines */}
        <div className="absolute top-20 left-0 w-full h-32 bg-gradient-to-r from-transparent via-jamaica-green/5 to-transparent transform -skew-y-1"></div>
        <div className="absolute bottom-20 left-0 w-full h-40 bg-gradient-to-r from-transparent via-jamaica-yellow/5 to-transparent transform skew-y-1"></div>

        {/* Hexagon Shapes */}
        <div className="absolute top-32 right-10 w-12 h-12 bg-jamaica-green/5 transform rotate-45 animate-pulse delay-500"></div>
        <div className="absolute top-1/2 left-10 w-8 h-8 bg-jamaica-yellow/10 transform rotate-12 animate-pulse delay-700"></div>
        <div className="absolute bottom-40 right-1/3 w-10 h-10 bg-jamaica-green/8 transform -rotate-12 animate-pulse delay-900"></div>

        {/* Quote Symbols */}
        <div className="absolute top-1/4 left-1/4 text-jamaica-green/10 text-6xl font-serif transform rotate-12 animate-pulse delay-1000">
          "
        </div>
        <div className="absolute bottom-1/3 right-1/4 text-jamaica-yellow/10 text-8xl font-serif transform -rotate-12 animate-pulse delay-1200">
          "
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Enhanced Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-3 mb-6 sm:mb-4">
            <div className="relative">
              <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gradient-to-br from-jamaica-yellow to-amber-500 rounded-2xl flex items-center justify-center shadow-xl transform rotate-12 landing-testimonial-icon">
                <MessageCircle className="w-4.5 h-4.5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="absolute -top-2 -right-2 w-5 h-5 sm:w-6 sm:h-6 bg-jamaica-green rounded-full flex items-center justify-center animate-bounce">
                <ThumbsUp className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
              </div>
            </div>
            <div className="bg-gradient-to-r from-jamaica-yellow/10 to-jamaica-green/10 px-3 py-1.5 sm:px-5 sm:py-2 rounded-2xl border border-jamaica-yellow/30 shadow-lg">
              <span className="text-xs sm:text-sm font-bold text-jamaica-black flex items-center gap-2">
                <Star className="w-4 h-4 text-jamaica-yellow fill-current" />
                Customer Love
              </span>
            </div>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            <span className="bg-gradient-to-r from-gray-900 via-jamaica-yellow to-gray-900 bg-clip-text text-transparent p-1">
              What Our Customers Say
            </span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Hear from people who have experienced our Jamaican care packages and
            felt the
            <span className="text-jamaica-yellow font-semibold">
              {" "}
              authentic island magic
            </span>
          </p>
        </motion.div>

        {/* Testimonials Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="relative group"
        >
          {/* Direct Carousel Container */}
          <div className="relative">
            {isReady && plugin.current && (
              <Carousel
                className="w-full"
                opts={{ align: "center", loop: true }}
                plugins={[plugin.current]}
              >
                <CarouselContent>
                  {testimonials.map((testimonial, index) => {
                    const linkedProduct = getLinkedProduct(
                      testimonial.productId
                    );

                    // Enhanced debugging for product linking
                    console.log(
                      `Testimonial ${index} (${testimonial.customerName}):`,
                      {
                        productId: testimonial.productId,
                        productIdType: typeof testimonial.productId,
                        linkedProduct: linkedProduct,
                        linkedProductExists: !!linkedProduct,
                        availableProducts: Object.keys(productsData),
                      }
                    );

                    return (
                      <CarouselItem key={testimonial.id}>
                        <div className="p-2">
                          {linkedProduct ? (
                            // Testimonial Card with Product - Two column layout
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                              {/* Left Side: Testimonial */}
                              <div className="group/testimonial relative">
                                <div className="relative bg-white/90 backdrop-blur-md rounded-2xl p-6 shadow-lg border-2 border-white/50 transform overflow-hidden landing-testimonial-card">
                                  {/* Quote Icon */}
                                  <div className="absolute top-4 right-4 opacity-20 group-hover/testimonial:opacity-40 transition-opacity duration-500">
                                    <Quote className="w-8 h-8 text-jamaica-green transform rotate-12" />
                                  </div>

                                  {/* Rating Stars */}
                                  <div className="flex mb-4 relative z-10">
                                    {[...Array(5)].map((_, i) => (
                                      <motion.div
                                        key={i}
                                        initial={{ scale: 0 }}
                                        whileInView={{ scale: 1 }}
                                        transition={{
                                          duration: 0.3,
                                          delay: index * 0.1 + i * 0.1,
                                        }}
                                        viewport={{ once: true }}
                                      >
                                        <Star
                                          className={`h-4 w-4 ${
                                            i < testimonial.rating
                                              ? "text-jamaica-yellow fill-jamaica-yellow"
                                              : "text-gray-300"
                                          }`}
                                        />
                                      </motion.div>
                                    ))}
                                    {testimonial.isVerified && (
                                      <div className="ml-2 bg-jamaica-green/10 px-2 py-1 rounded-full">
                                        <span className="text-xs font-semibold text-jamaica-green">
                                          ✓ Verified
                                        </span>
                                      </div>
                                    )}
                                  </div>

                                  {/* Testimonial Content */}
                                  <blockquote className="text-gray-700 mb-4 leading-relaxed italic relative z-10 group-hover/testimonial:text-gray-800 transition-colors duration-300 text-sm">
                                    "{testimonial.content}"
                                  </blockquote>

                                  {/* Author Info */}
                                  <div className="flex items-center gap-3 relative z-10">
                                    {/* Avatar */}
                                    <div
                                      className={`w-10 h-10 bg-gradient-to-br ${getAvatarBackgroundColor(
                                        testimonial.bgColor
                                      )} rounded-full flex items-center justify-center shadow-lg transform group-hover/testimonial:scale-110 transition-transform duration-300`}
                                    >
                                      <span className="text-white font-bold text-base">
                                        {testimonial.avatar ||
                                          getCustomerInitials(
                                            testimonial.customerName
                                          )}
                                      </span>
                                    </div>

                                    {/* Author Details */}
                                    <div className="flex-1">
                                      <p className="font-bold text-gray-900 group-hover/testimonial:text-jamaica-green transition-colors duration-300 text-sm">
                                        {testimonial.customerName}
                                      </p>
                                      {testimonial.customerLocation && (
                                        <div className="flex items-center gap-1 text-xs text-gray-500">
                                          <MapPin className="w-3 h-3" />
                                          <span>
                                            {testimonial.customerLocation}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Hover Gradient Background */}
                                  <div className="absolute inset-0 bg-gradient-to-br from-jamaica-green/3 via-transparent to-jamaica-yellow/3 opacity-0 group-hover/testimonial:opacity-100 transition-opacity duration-500 rounded-2xl"></div>
                                </div>
                              </div>

                              {/* Right Side: Linked Product */}
                              <div className="group/product relative">
                                <div className="relative bg-white/95 backdrop-blur-md rounded-2xl p-4 shadow-lg border-2 border-white/50 overflow-hidden">
                                  {/* Product Badges - matching ProductCard component */}
                                  <div className="absolute top-3 left-3 z-30 flex flex-wrap gap-1">
                                    {linkedProduct.isNew && (
                                      <span className="bg-jamaica-green text-white text-xs font-semibold px-2 py-1 rounded-full backdrop-blur-sm shadow-sm">
                                        NEW
                                      </span>
                                    )}
                                    {linkedProduct.isBestSeller && (
                                      <span className="bg-jamaica-yellow text-jamaica-black text-xs font-semibold px-2 py-1 rounded-full backdrop-blur-sm shadow-sm">
                                        BEST SELLER
                                      </span>
                                    )}
                                    {linkedProduct.onSale && (
                                      <span className="bg-jamaica-black text-jamaica-yellow text-xs font-semibold px-2 py-1 rounded-full backdrop-blur-sm shadow-sm">
                                        ON SALE
                                      </span>
                                    )}
                                  </div>

                                  {/* Product Image with Overlaid Info */}
                                  <Link
                                    to={`/product/${linkedProduct.id}`}
                                    className="block relative z-20 group-hover/product:scale-105 transition-transform duration-500"
                                  >
                                    <div className="relative aspect-square rounded-xl overflow-hidden">
                                      <ProductImage
                                        src={linkedProduct.image}
                                        alt={linkedProduct.name}
                                        className="w-full h-full object-cover"
                                        fallbackSrc="/lovable-uploads/bf2869c6-c7dd-4a46-a046-7189f9435aa3.png"
                                        aspectRatio="square"
                                      />

                                      {/* Bottom Gradient Overlay for Text */}
                                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3 rounded-b-xl z-10">
                                        <h3 className="font-bold text-sm text-white transition-colors duration-300 line-clamp-2 mb-1">
                                          {linkedProduct.name}
                                        </h3>

                                        {/* Price Display */}
                                        <div className="flex items-center gap-2">
                                          {linkedProduct.onSale &&
                                          "onSalePrice" in linkedProduct &&
                                          linkedProduct.onSalePrice ? (
                                            <>
                                              <span className="text-jamaica-yellow font-bold text-base bg-black/30 px-2 py-1 rounded-lg backdrop-blur-sm">
                                                {formatPrice(linkedProduct.onSalePrice)} {selectedCurrency}
                                              </span>
                                              <span className="text-white/70 text-xs line-through bg-black/20 px-1 py-1 rounded backdrop-blur-sm">
                                                {formatPrice(linkedProduct.price)}
                                              </span>
                                            </>
                                          ) : (
                                            <span className="text-white font-bold text-base bg-black/30 px-2 py-1 rounded-lg backdrop-blur-sm">
                                              {formatPrice(linkedProduct.price)} {selectedCurrency}
                                            </span>
                                          )}
                                        </div>
                                      </div>

                                      {/* Hover Overlay */}
                                      <div className="absolute inset-0 bg-gradient-to-t from-jamaica-green/40 to-transparent opacity-0 group-hover/product:opacity-100 transition-all duration-500 flex items-center justify-center z-10">
                                        <div className="bg-white/90 backdrop-blur-sm text-jamaica-green font-bold px-3 py-1 rounded-lg shadow-xl border border-white/50 transform translate-y-4 group-hover/product:translate-y-0 transition-transform duration-500 pointer-events-none">
                                          <div className="flex items-center gap-1 text-xs">
                                            <ShoppingBag className="w-3 h-3" />
                                            View Product
                                            <ArrowRight className="w-3 h-3" />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </Link>

                                  {/* Decorative Elements */}
                                  <div className="absolute bottom-3 right-3 w-4 h-4 border-2 border-jamaica-yellow/30 rounded-full opacity-0 group-hover/product:opacity-100 transition-all duration-500 delay-200 z-10 pointer-events-none"></div>
                                  <div className="absolute top-12 left-3 w-3 h-3 bg-jamaica-green/20 rounded-full opacity-0 group-hover/product:opacity-100 transition-all duration-500 delay-300 animate-pulse z-10 pointer-events-none"></div>

                                  {/* Hover Gradient Background */}
                                  <div className="absolute inset-0 bg-gradient-to-br from-jamaica-yellow/3 via-transparent to-jamaica-green/3 opacity-0 group-hover/product:opacity-100 transition-opacity duration-500 rounded-2xl z-0 pointer-events-none"></div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            // Testimonial Only - Centered single card
                            <div className="flex justify-center">
                              <div className="group/testimonial relative max-w-lg w-full">
                                <div className="relative bg-white/90 backdrop-blur-md rounded-2xl p-6 shadow-lg border-2 border-white/50 transform overflow-hidden landing-testimonial-card">
                                  {/* Quote Icon */}
                                  <div className="absolute top-4 right-4 opacity-20 group-hover/testimonial:opacity-40 transition-opacity duration-500">
                                    <Quote className="w-8 h-8 text-jamaica-green transform rotate-12" />
                                  </div>

                                  {/* Rating Stars */}
                                  <div className="flex mb-4 relative z-10">
                                    {[...Array(5)].map((_, i) => (
                                      <motion.div
                                        key={i}
                                        initial={{ scale: 0 }}
                                        whileInView={{ scale: 1 }}
                                        transition={{
                                          duration: 0.3,
                                          delay: index * 0.1 + i * 0.1,
                                        }}
                                        viewport={{ once: true }}
                                      >
                                        <Star
                                          className={`h-4 w-4 ${
                                            i < testimonial.rating
                                              ? "text-jamaica-yellow fill-jamaica-yellow"
                                              : "text-gray-300"
                                          }`}
                                        />
                                      </motion.div>
                                    ))}
                                    {testimonial.isVerified && (
                                      <div className="ml-2 bg-jamaica-green/10 px-2 py-1 rounded-full">
                                        <span className="text-xs font-semibold text-jamaica-green">
                                          ✓ Verified
                                        </span>
                                      </div>
                                    )}
                                  </div>

                                  {/* Testimonial Content */}
                                  <blockquote className="text-gray-700 mb-4 leading-relaxed italic relative z-10 group-hover/testimonial:text-gray-800 transition-colors duration-300 text-sm">
                                    "{testimonial.content}"
                                  </blockquote>

                                  {/* Author Info */}
                                  <div className="flex items-center gap-3 relative z-10">
                                    {/* Avatar */}
                                    <div
                                      className={`w-10 h-10 bg-gradient-to-br ${getAvatarBackgroundColor(
                                        testimonial.bgColor
                                      )} rounded-full flex items-center justify-center shadow-lg transform group-hover/testimonial:scale-110 transition-transform duration-300`}
                                    >
                                      <span className="text-white font-bold text-base">
                                        {testimonial.avatar ||
                                          getCustomerInitials(
                                            testimonial.customerName
                                          )}
                                      </span>
                                    </div>

                                    {/* Author Details */}
                                    <div className="flex-1">
                                      <p className="font-bold text-gray-900 group-hover/testimonial:text-jamaica-green transition-colors duration-300 text-sm">
                                        {testimonial.customerName}
                                      </p>
                                      {testimonial.customerLocation && (
                                        <div className="flex items-center gap-1 text-xs text-gray-500">
                                          <MapPin className="w-3 h-3" />
                                          <span>
                                            {testimonial.customerLocation}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Hover Gradient Background */}
                                  <div className="absolute inset-0 bg-gradient-to-br from-jamaica-green/3 via-transparent to-jamaica-yellow/3 opacity-0 group-hover/testimonial:opacity-100 transition-opacity duration-500 rounded-2xl"></div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </CarouselItem>
                    );
                  })}
                </CarouselContent>

                {/* Custom Navigation Buttons */}
                <CarouselPrevious className="absolute -left-12 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm border-2 border-jamaica-green/20 hover:border-jamaica-green hover:bg-jamaica-green hover:text-white shadow-xl" />
                <CarouselNext className="absolute -right-12 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm border-2 border-jamaica-green/20 hover:border-jamaica-green hover:bg-jamaica-green hover:text-white shadow-xl" />
              </Carousel>
            )}
          </div>

          {/* Enhanced Decorative elements */}
          <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-gradient-to-br from-jamaica-yellow/20 to-jamaica-yellow/10 rounded-full blur-2xl animate-pulse delay-500"></div>
          <div className="absolute -top-6 -right-6 w-28 h-28 bg-gradient-to-br from-jamaica-green/20 to-jamaica-green/10 rounded-full blur-2xl animate-pulse delay-700"></div>
          <div className="absolute top-1/2 -left-4 w-16 h-16 bg-jamaica-yellow/15 rounded-2xl blur-lg animate-bounce delay-300 transform rotate-45"></div>
          <div className="absolute bottom-1/4 -right-4 w-20 h-20 bg-jamaica-green/15 rounded-2xl blur-lg animate-pulse delay-900 transform -rotate-12"></div>
        </motion.div>

        {/* Bottom Stats */}
        {/* <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 text-center"
        >
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/30">
            <div className="text-3xl font-bold text-jamaica-green mb-2">500+</div>
            <div className="text-gray-600">Happy Customers</div>
            </div>
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/30">
            <div className="text-3xl font-bold text-jamaica-yellow mb-2">4.9★</div>
            <div className="text-gray-600">Average Rating</div>
            </div>
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/30">
            <div className="text-3xl font-bold text-jamaica-green mb-2">98%</div>
            <div className="text-gray-600">Would Recommend</div>
          </div>
        </motion.div> */}
      </div>
    </div>
  );
};

export default Testimonials;
